import {
    Box,
    Button,
    Field,
    Flex,
    Icon,
    IconButton,
    Input,
    InputGroup,
    Spacer,
    useBreakpointValue,
} from "@chakra-ui/react";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react/dist/ssr";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLeadingMaps } from "@/hooks/useBuscadorHook";
import { buildUrlSearchParams } from "@/pages/Buscar/useSearchState";

import { computePlaceRadius } from "./computePlaceRadius";
import BedsBathsPopover from "./filters/BedsBathsPopover";
import MoreFiltersPopover from "./filters/MoreFiltersPopover";
import { currentOperationValue, OPERATION_OPTIONS } from "./filters/OperationFilter";
import PricePopover from "./filters/PricePopover";
import PropertyTypePopover from "./filters/PropertyTypePopover";
import { pointInAnyPolygon } from "./pointInPolygon";
import ResetFiltersButton from "./ResetFiltersButton";
import { resolveFirstPrediction } from "./resolveFirstPrediction";
import { useSearchPage } from "./SearchPageContext";

const LocationInput = ({ state, setFilters, applyPlaceBoundary }) => {
    // Dedicated-page identity (null on /Buscar): supplies the page's default
    // radius for input-clear and the local autocomplete bias below.
    const searchPage = useSearchPage();
    // On a city page, bias predictions to the page boundary's bbox instead
    // of the Mexico-wide default (soft ranking only — out-of-city places
    // remain reachable and are handled explicitly on pick).
    const biasBounds = searchPage?.config?.floor?.bbox ?? null;
    // Router is only used by the out-of-boundary escape redirect.
    const router = useRouter();
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [query] = useLeadingMaps(autocompleteRef, inputRef, { biasBounds });
    const [value, setValue] = useState(state.q || "");
    // Below lg the field shrinks; a shorter placeholder fits without ellipsis.
    // ssr: false because Chakra v3's default SSR fallback picks the LAST defined
    // entry, which would flash the wrong copy on first paint at non-lg widths.
    const placeholder =
        useBreakpointValue(
            {
                base: "Ubicación",
                lg: "Buscar ubicación",
            },
            { ssr: typeof window === "undefined" }
        ) ?? "Buscar ubicación";
    // Track which query object we've already pushed into filter state. Without
    // this, the application useEffect re-fires when setFilters' reference
    // changes (which happens on every state update because syncUrl depends on
    // router and router updates on shallow navigation) and re-applies the
    // *stale* `query` from useLeadingMaps — undoing onClear's reset within a
    // tick. The ref makes application strictly one-shot per place pick.
    const appliedQueryRef = useRef(null);

    // Apply a Google PlaceResult: extract lat/lng, viewport-derived radius,
    // neighborhood/city, then push everything into filter state + the
    // boundary visual. Single source of truth used by both the Places-
    // autocomplete path (place_changed → query effect) AND the Enter-
    // resolves-first-prediction path (P6.9 — typed text + Enter without
    // a dropdown pick → AutocompleteService → PlacesService → here).
    //
    // Full rules + rationale in epic DECISIONS.md D15 (radius math),
    // D17 (boundary visual), D18.b (Enter-resolves).
    const applyResolvedPlace = useCallback(
        (place) => {
            if (!place?.geometry?.location) return;
            const findComp = (type) =>
                place.address_components?.find((c) => c.types.includes(type))?.long_name || "";
            const neighborhood = findComp("sublocality_level_1");
            const city = findComp("locality");

            const latitude = place.geometry.location.lat();
            const longitude = place.geometry.location.lng();
            // Display name preference: when sublocality is present, use
            // place.name (the colonia's actual name); otherwise fall back
            // to locality → place.name → formatted_address.
            const displayName =
                (neighborhood && place.name) || city || place.name || place.formatted_address || "";

            // Visual radius via the shared computePlaceRadius helper — same
            // formula used by the homepage submit URL builder so the circle is
            // identical across both entry paths. apiBody adds the grace buffer
            // on top for the backend filter (visual stays strict).
            const { rendered: resolvedRadius, raw: rawRadius } = computePlaceRadius(place, {
                hasNeighborhood: Boolean(neighborhood),
            });

            // The filter patch this pick produces — one shape for both the
            // apply-here and the escape-redirect paths below.
            const patch = neighborhood
                ? {
                      // Strict colonia match — backend filters by name. City
                      // sent too for the rare same-named neighborhood across
                      // municipios.
                      q: displayName,
                      latitude,
                      longitude,
                      zoom: 14,
                      neighborhood,
                      city,
                      radius: resolvedRadius,
                  }
                : {
                      // Geographic narrow via viewport-sized radius — keeps
                      // Zapopan/Tlaquepaque in scope for a "Guadalajara"
                      // search.
                      q: displayName,
                      latitude,
                      longitude,
                      zoom: city ? 12 : 11,
                      neighborhood: "",
                      city: "",
                      radius: resolvedRadius,
                  };

            // Escape hatch for dedicated city pages: a pick whose centroid
            // falls outside every boundary ring can't be honestly shown
            // under this page's identity — hand the whole search to the
            // general page instead of rendering an empty city-branded list.
            // The URL carries the user's non-default filters plus the place
            // params (place wins collisions via the patch overlay); the
            // boundary itself never travels, and neither do drawn polygons —
            // a place pick replaces the boundary stack in the normal flow,
            // so the redirect mirrors that by dropping them.
            const floorRings = searchPage?.config?.floor?.polygons;
            if (
                floorRings?.length &&
                !pointInAnyPolygon({ lat: latitude, lng: longitude }, floorRings)
            ) {
                const params = buildUrlSearchParams(
                    { ...state, ...patch, polygons: [] },
                    searchPage.DEFAULTS
                );
                router.push(`/Buscar?${params.toString()}`);
                return;
            }

            setValue(displayName);
            setFilters(patch);
            // Camera fits the circle (its own bounds, not the viewport rect)
            // and the circle paints over it. rawRadius is forwarded for the
            // debug overlay only (Google's pre-clamp half-diagonal — gone
            // from prod flow, kept only for ?debug=1).
            applyPlaceBoundary?.({
                center: { lat: latitude, lng: longitude },
                radiusM: resolvedRadius,
                rawRadiusM: rawRadius,
            });
        },
        [setFilters, applyPlaceBoundary, searchPage, state, router]
    );

    // Live autocomplete pick path. useLeadingMaps' `query` updates when the
    // user picks from the Places dropdown; we just translate that into the
    // raw PlaceResult and feed it to applyResolvedPlace.
    useEffect(() => {
        if (!query || !query.latitude || !query.longitude) return;
        if (appliedQueryRef.current === query) return; // already applied this pick
        appliedQueryRef.current = query;
        const place = autocompleteRef.current?.getPlace?.();
        if (place?.geometry?.location) applyResolvedPlace(place);
    }, [query, applyResolvedPlace]);

    // Re-sync from external state (URL refresh / programmatic location change).
    useEffect(() => {
        setValue(state.q || "");
    }, [state.q]);

    // P6.9 Enter-resolves-first-prediction (D18.b): user types text and
    // hits Enter without clicking a dropdown row → resolve via the Places
    // API directly. Same resolveFirstPrediction helper as the homepage's
    // BuscadorDesktop so both surfaces resolve identically. Soft falls
    // back to raw `q` text on any failure.
    const handleKeyDown = async (e) => {
        if (e.key !== "Enter") return;
        const trimmed = inputRef.current?.value?.trim();
        if (!trimmed) return;
        const place = await resolveFirstPrediction(trimmed, { biasBounds });
        if (place) {
            applyResolvedPlace(place);
        } else {
            setFilters({ q: trimmed });
        }
    };

    const onClear = () => {
        setValue("");
        if (inputRef.current) inputRef.current.value = "";
        setFilters({
            q: "",
            latitude: null,
            longitude: null,
            zoom: null,
            neighborhood: "",
            city: "",
            radius: searchPage?.DEFAULTS?.radius ?? 5000,
        });
        // Clear the boundary overlay too. Matches Zillow: removing the
        // location text also drops the circle (the visual is anchored to
        // the location). Distinct from P6.3 "Borrar perímetro" which clears
        // boundary + location together from the map side.
        applyPlaceBoundary?.(null);
    };

    return (
        <Field.Root
            // flex={1} at xs/sm lets the input grow to fill the row. minW
            // keeps it usable: at xs it forces the compact trigger buttons
            // (Más filtros + Limpiar) to wrap to the next row when there
            // isn't enough horizontal space, giving the search bar priority.
            // At sm-md it stops the input from collapsing to invisibility
            // when the operation segmented + Más filtros + Limpiar are all
            // competing for the row. From md+ it goes back to fixed width.
            flex={{ base: 1, md: "0 0 auto" }}
            minW={{ base: "220px", sm: "260px", md: undefined }}
            w={{ base: "auto", md: "240px", lg: "320px" }}
        >
            <InputGroup
                startOffset="6px"
                startElementProps={{ pl: 2, pointerEvents: "none" }}
                startElement={
                    <Icon color="gray.400" boxSize={4}>
                        <MagnifyingGlassIcon weight="bold" />
                    </Icon>
                }
                endElement={
                    value ? (
                        <IconButton
                            aria-label="Limpiar búsqueda"
                            onClick={onClear}
                            size="xs"
                            variant="ghost"
                            borderRadius="full"
                            color="gray.500"
                            _hover={{ bg: "gray.100", color: "gray.700" }}
                        >
                            <Icon boxSize={3}>
                                <XIcon weight="bold" />
                            </Icon>
                        </IconButton>
                    ) : undefined
                }
            >
                <Input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    fontSize="sm"
                    h="40px"
                    borderRadius="md"
                    bg="white"
                />
            </InputGroup>
        </Field.Root>
    );
};

const OperationSegmented = ({ state, setFilter }) => {
    const current = currentOperationValue(state.operation);
    const onPick = (value) => {
        if (value === "ALL") {
            // D18.a: Todas is the explicit "all operations" pick, stored as
            // [] internally and serialized to URL as `operacion=todas`. NOT
            // the default — the default is Comprar (handled by isDefault on
            // the [DEFAULT_OPERATION] state value).
            setFilter("operation", []);
            return;
        }
        setFilter("operation", [value]);
    };
    return (
        <Flex borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" h="40px">
            {OPERATION_OPTIONS.map((opt) => {
                const active = opt.value === current;
                return (
                    <Button
                        key={opt.value}
                        onClick={() => onPick(opt.value)}
                        h="40px"
                        px={4}
                        fontSize="sm"
                        fontWeight={active ? 600 : 500}
                        bg={active ? "colorPalette.solid" : "white"}
                        color={active ? "white" : "gray.700"}
                        borderRadius={0}
                        _hover={{ bg: active ? "colorPalette.solid" : "gray.50" }}
                    >
                        {opt.label}
                    </Button>
                );
            })}
        </Flex>
    );
};

const TopBar = ({ state, setFilter, clearFilter, setFilters, applyPlaceBoundary }) => {
    return (
        <Flex
            position="sticky"
            top="80px"
            zIndex={20}
            bg="white"
            borderBottomWidth="1px"
            borderColor="gray.200"
            px={4}
            py={3}
            gap={2}
            alignItems="center"
            flexWrap="wrap"
        >
            <LocationInput
                state={state}
                setFilters={setFilters}
                applyPlaceBoundary={applyPlaceBoundary}
            />
            {/* Operation segmented hides below md; it's absorbed into the
                Más filtros bottom drawer there, where it commits with the
                global Aplicar like the other absorbed sections. The icon-
                only filter trigger at xs–sm keeps the bar to a single row
                + tight footprint; segmented re-emerges at md. */}
            <Box hideBelow="md">
                <OperationSegmented state={state} setFilter={setFilter} />
            </Box>
            {/* At <lg the bar can't fit all 4 pills cleanly. Hide Price/Beds/Type
                here — MoreFiltersPopover renders their bodies absorbed when its
                drawer opens at the same breakpoint. */}
            <Box hideBelow="lg">
                <PricePopover state={state} setFilter={setFilter} clearFilter={clearFilter} />
            </Box>
            <Box hideBelow="lg">
                <PropertyTypePopover
                    state={state}
                    setFilter={setFilter}
                    clearFilter={clearFilter}
                />
            </Box>
            <Box hideBelow="lg">
                <BedsBathsPopover state={state} setFilter={setFilter} clearFilter={clearFilter} />
            </Box>
            <MoreFiltersPopover state={state} setFilter={setFilter} clearFilter={clearFilter} />
            {/* Spacer eats remaining width via flex 1 — fine at md+ where it
                pushes ResetFiltersButton to the right. At xs–sm it competes
                with the search input's flex={1}, splitting the leftover space
                and shrinking the input. Hide it there. */}
            <Spacer hideBelow="md" />
            <ResetFiltersButton state={state} setFilters={setFilters} />
        </Flex>
    );
};

export default TopBar;
